import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { createColumnsFromFields } from '../../../../pages/contactsExtended/contactFields/tableColumns';
import { trackEvent } from '../../../../helpers/analytics';
import { deserializeFieldName, serializeFieldName } from '../../../../pages/contactsExtended/contactFields/fields';
import { initializeVisibleFields } from '../../../../pages/contactsExtended/contactFields/utils';
import { useContactVisibleFields } from '../../../../pages/contactsExtended/contactFields/useContactVisibleFields';
import { getContactsColumnsKey } from '../../../../helpers/storeKeys';
import { jobApplicantsJobTnSelector } from '../../../../redux/selectors';
import { useGetCustomFieldsQuery } from '../../../../services/customFieldsV2';

// Fields that should be hidden by default in shared views for privacy reasons
const SHARED_VIEW_HIDDEN_BY_DEFAULT = new Set(['workEmail', 'personalEmail']);

const serializeFields = (fields) =>
  fields.map((field) => ({
    fieldId: field.accessor.startsWith('custom-field-')
      ? parseInt(field.accessor.replace('custom-field-', ''), 10)
      : serializeFieldName(field.accessor),
    visible: field.show,
  }));

const deserializeFields = (apiFields, availableFields) => {
  if (!apiFields || apiFields.length === 0 || (apiFields.length === 1 && apiFields[0] === 'all')) {
    return availableFields.map((field) => ({ accessor: field.accessor, show: true }));
  }

  const parsedFields = apiFields
    .map((field) => ({
      accessor:
        typeof field.fieldId === 'number'
          ? `custom-field-${field.fieldId}`
          : deserializeFieldName(field.fieldId) || field.fieldId,
      show: field.visible,
    }))
    .filter((field) => availableFields.some((col) => col.accessor === field.accessor));

  const savedAccessors = new Set(parsedFields.map((f) => f.accessor));
  availableFields.forEach((field) => {
    if (!savedAccessors.has(field.accessor)) {
      parsedFields.push({ accessor: field.accessor, show: true });
    }
  });

  return parsedFields;
};

export const useFieldVisibility = ({ list, activeNetwork, updateView, viewId }) => {
  const joinTnFromJB = useSelector(jobApplicantsJobTnSelector);
  const listIdString = list?.id ? String(list.id) : null;

  // TN is enabled if tnHidden is false (tnHidden=true means talent network is not available)
  const isTalentNetworkEnabled = !activeNetwork?.tnHidden;

  const { data: customFields = [] } = useGetCustomFieldsQuery(
    {
      includeLists: !!listIdString,
      ...(listIdString ? { sharedListId: listIdString } : undefined),
    },
    { skip: !listIdString },
  );
  const [visibleFields] = useContactVisibleFields(customFields, {
    isPublicListView: false,
    isTalentNetworkEnabled,
    fieldSettings: null,
    isListView: true,
  });

  const availableFields = useMemo(
    () =>
      createColumnsFromFields(visibleFields, customFields).filter(
        (col) => col.accessor !== 'name' && col.accessor !== 'resume' && !col.hideOnTableColumn,
      ),
    [visibleFields, customFields],
  );

  const fieldsState = useMemo(() => {
    if (!list?.views) {
      return availableFields.map((field) => ({ accessor: field.accessor, show: true }));
    }

    const sharedView = list.views.find((view) => view.shareable === true);
    if (!sharedView?.fieldSettings) {
      return availableFields.map((field) => ({ accessor: field.accessor, show: true }));
    }

    return deserializeFields(sharedView.fieldSettings, availableFields);
  }, [list?.views, availableFields]);

  const onFieldsChange = useCallback(
    async (newFields) => {
      if (!updateView || !viewId) return;

      const serialized = serializeFields(newFields);

      try {
        await updateView({
          sharedListId: list.id,
          id: viewId,
          data: {
            fieldSettings: serialized,
          },
        });

        const visibleCount = newFields.filter((f) => f.show).length;
        trackEvent('contacts:list:change_field_visibility', {
          collection_id: activeNetwork.id,
          list_id: list.id,
          visible_fields_count: visibleCount,
        });
      } catch (error) {
        // Error already handled by updateViewWithToast
      }
    },
    [activeNetwork.id, list.id, updateView, viewId],
  );

  const getInitialFields = useCallback(() => {
    const initialFields = [];
    const detailViewColumnsKey = getContactsColumnsKey({
      activeNetwork,
      type: 'list',
      joinTnFromJB,
      listId: list?.id,
    });
    const initialVisibleFields = initializeVisibleFields(customFields, {
      isPublicListView: false,
      isTalentNetworkEnabled,
      fieldSettings: null,
      storageKey: detailViewColumnsKey,
      isListView: true,
    });

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of initialVisibleFields.entries()) {
      if (key !== 'name') {
        const shouldShow = SHARED_VIEW_HIDDEN_BY_DEFAULT.has(key) ? false : value.show;
        initialFields.push({ accessor: key, show: shouldShow });
      }
    }

    return initialFields;
  }, [activeNetwork, joinTnFromJB, list?.id, customFields, isTalentNetworkEnabled]);

  const getInitialFieldsSerialized = useCallback(() => serializeFields(getInitialFields()), [getInitialFields]);

  return {
    availableFields,
    fieldsState,
    onFieldsChange,
    getInitialFields,
    getInitialFieldsSerialized,
  };
};
